import { computed, type Ref, ref } from 'vue'

import {
  type LinkClickCpcItem,
  normalizeLinkClickCpcItem,
  type serializeLinkClicksCpcPayload,
} from '@/lib/adapters/link-clicks-cpc.adapter'
import { useApi } from '@/lib/helpers/api.helpers'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import type { MoneyServerType } from '@/lib/types/common.types'
import { useErrorStore } from '@/stores/error'

export type LinkClick = {
  id: number
  name: string
  count: number
}

type LinkClicksResponse = {
  link_clicks: LinkClick[]
  percent: number
  in_open_payment_cycle?: boolean
}

export const useGetLinkClicks = (tagIdRef: Ref<number>) => {
  const {
    data,
    isLoading,
    error,
    fetchData: refresh,
  } = useApi(async () => {
    const response = await request.get<LinkClicksResponse>(
      `/admin/tags/${tagIdRef.value}/link-clicks`,
    )
    return response.data
  })
  return { data, isLoading, error, refresh }
}

export const useRecalculateLinkClicks = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const recalculate = async (
    tagId: number,
    percent: number,
  ): Promise<LinkClicksResponse | null> => {
    try {
      isLoading.value = true
      error.value = null
      const response = await request.post<LinkClicksResponse>(
        `/admin/tags/${tagId}/link-clicks/recalculate`,
        { percent },
      )
      return response.data
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return null
    } finally {
      isLoading.value = false
    }
  }

  return { recalculate, isLoading, error }
}

export type LinkClickCpc = {
  count: number
  source: number
  offer_ids: number[] | null
  user_revenue_cpc_v2: MoneyServerType
}

type LinkClicksCpcResponse = {
  link_clicks_cpc: LinkClickCpc[]
  in_open_payment_cycle?: boolean
}

export const useGetLinkClicksCpc = (tagIdRef: Ref<number>) => {
  const {
    data: response,
    isLoading,
    error,
    fetchData: refresh,
  } = useApi(async () => {
    const r = await request.get<LinkClicksCpcResponse>(
      `/admin/tags/${tagIdRef.value}/link-clicks-cpc`,
    )
    return r.data
  })

  const items = computed<LinkClickCpcItem[]>(() =>
    (response.value?.link_clicks_cpc ?? []).map(normalizeLinkClickCpcItem),
  )

  return { items, isLoading, error, refresh }
}

export const useRecalculateLinkClicksCpc = () => {
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const recalculate = async (
    tagId: number,
    payload: ReturnType<typeof serializeLinkClicksCpcPayload>,
  ): Promise<boolean> => {
    try {
      isLoading.value = true
      error.value = null
      await request.post(`/admin/tags/${tagId}/link-clicks-cpc/recalculate`, payload)
      return true
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
      return false
    } finally {
      isLoading.value = false
    }
  }

  return { recalculate, isLoading, error }
}
